import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string | null;
  canonical?: string;
  type?: 'website' | 'article' | 'product';
  jsonLd?: Record<string, unknown>;
  noIndex?: boolean;
}

/**
 * 🔍 مكوّن SEO ديناميكي
 *
 * يحدّث وسوم <head> بدون react-helmet (لتجنب dependency إضافية).
 * - title
 * - meta description
 * - meta keywords
 * - canonical
 * - Open Graph (og:*)
 * - Twitter Cards
 * - JSON-LD structured data
 *
 * الاستخدام:
 *   <SEO
 *     title="فلل للبيع في الرياض"
 *     description="..."
 *     keywords="فلل، الرياض، للبيع"
 *     image="/og.jpg"
 *     jsonLd={{ '@type': 'RealEstateListing', ... }}
 *   />
 */
const SITE_NAME = 'شركة صورة العقارية';
const BRAND_SLOGAN = 'العقار بصورة جديدة';
const SITE_URL = 'https://img.sa';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION = `${BRAND_SLOGAN} — منصة شركة صورة العقارية للعقارات والمزادات والاستثمار في المملكة العربية السعودية. ${BRAND_SLOGAN}.`;

function setMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.querySelector<HTMLScriptElement>(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify({ '@context': 'https://schema.org', ...data });
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  image = DEFAULT_IMAGE,
  canonical,
  type = 'website',
  jsonLd,
  noIndex = false,
}: SEOProps) {
  useEffect(() => {
    // 🎯 ضمّن "العقار بصورة جديدة" في كل title — يساعد جوجل يربط العبارة بالموقع
    const fullTitle = title
      ? `${title} | ${SITE_NAME} — ${BRAND_SLOGAN}`
      : `${SITE_NAME} — ${BRAND_SLOGAN} | العقارات والمزادات في السعودية`;
    document.title = fullTitle;

    // 🎯 ضمّن الشعار في كل وصف لو ما هو موجود
    const finalDescription = description.includes(BRAND_SLOGAN)
      ? description
      : `${description} — ${BRAND_SLOGAN}.`;

    setMeta('description', finalDescription);
    if (keywords) {
      // 🎯 ضمّن الشعار كأول كلمة مفتاحية — أعلى وزن SEO
      const finalKeywords = keywords.includes(BRAND_SLOGAN) ? keywords : `${BRAND_SLOGAN}, ${keywords}`;
      setMeta('keywords', finalKeywords);
    } else {
      setMeta('keywords', `${BRAND_SLOGAN}, شركة صورة العقارية, عقارات السعودية, مزادات عقارية`);
    }
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large');

    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', finalDescription, true);
    setMeta('og:type', type, true);
    setMeta('og:url', canonical ?? window.location.href, true);
    if (image) setMeta('og:image', image, true);
    setMeta('og:site_name', `${SITE_NAME} — ${BRAND_SLOGAN}`, true);
    setMeta('og:locale', 'ar_SA', true);

    // Twitter
    setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', finalDescription);
    if (image) setMeta('twitter:image', image);

    // Canonical
    setLink('canonical', canonical ?? window.location.href);

    // 🎯 JSON-LD المخصّص للصفحة
    if (jsonLd) setJsonLd('page', jsonLd);

    // 🏛️ JSON-LD ثابت للمنظمة + الشعار "العقار بصورة جديدة" — على كل صفحة
    setJsonLd('organization', {
      '@type': 'Organization',
      '@id': `${SITE_URL}#organization`,
      name: SITE_NAME,
      alternateName: ['Sora Real Estate Company', 'IMG SA', 'صورة العقارية'],
      slogan: BRAND_SLOGAN,
      description: `${SITE_NAME} — ${BRAND_SLOGAN}. منصة عقارية سعودية متكاملة معتمدة من الهيئة العامة للعقار.`,
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      foundingDate: '2016',
      knowsAbout: ['العقار بصورة جديدة', 'عقارات السعودية', 'مزادات عقارية', 'اتحاد الملاك'],
      address: { '@type': 'PostalAddress', addressCountry: 'SA' },
      contactPoint: [{
        '@type': 'ContactPoint',
        telephone: '+966114993666',
        contactType: 'customer service',
        areaServed: 'SA',
        availableLanguage: ['Arabic', 'English'],
      }],
    });
  }, [title, description, keywords, image, canonical, type, jsonLd, noIndex]);

  return null;
}
